(() => {
  'use strict';
  const code = '10vapediscount_fspJED1274KFJHAI71';
  const normalize = value => String(value || '').trim().toLowerCase();
  const giftHandles = new Set(['free-gift-comfort-thc-gummies', 'free-gift-sativa-vape']);
  let active = false;
  let cleanup = null;
  let checkoutBusy = false;
  let replayTarget = null;
  const root = window.Shopify?.routes?.root || '/';

  function setActive(value) {
    active = value;
    document.documentElement.classList.toggle('rasta-vape-offer-no-gifts', active);
    return active;
  }

  // Shopify's /discount link redirects without a query parameter. Read only
  // its discount cookie until the cart API supplies the authoritative codes.
  const cookie = document.cookie.split(';').map(value => value.trim()).find(value => value.startsWith('discount_code='));
  if (cookie) {
    try { setActive(normalize(decodeURIComponent(cookie.slice('discount_code='.length))) === normalize(code)); }
    catch (_) { /* A malformed cookie must not interrupt the storefront. */ }
  }

  function observeCart(cart) {
    if (Array.isArray(cart?.discount_codes)) {
      setActive(cart.discount_codes.some(discount => normalize(discount.code) === normalize(code)));
    }
    return active;
  }

  function isGift(item) {
    return String(item?.properties?._cs_free_gift) === 'true' || giftHandles.has(item?.handle);
  }

  async function request(path, options = {}) {
    const response = await fetch(root + path, { credentials: 'same-origin', cache: 'no-store', ...options });
    if (!response.ok) throw new Error('We could not prepare your offer. Please try checkout again.');
    const cart = await response.json();
    if (!Array.isArray(cart?.items)) throw new Error('We could not verify your cart. Please try checkout again.');
    observeCart(cart);
    return cart;
  }

  function notifyCartUpdated() {
    const notify = () => document.dispatchEvent(new CustomEvent('cart:updated'));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', notify, { once: true });
    else notify();
  }

  function removeGifts(cart) {
    if (cleanup) return cleanup;
    cleanup = (async () => {
      let current = cart || await request('cart.js');
      let changed = false;
      // Recheck after each removal: checkout must never continue on a failed
      // update or while another gift sync is still adding a gift line.
      for (let attempt = 0; attempt < 3; attempt++) {
        if (!observeCart(current)) break;
        const gifts = current.items.filter(isGift);
        if (!gifts.length) break;
        if (gifts.some(item => !item.key)) throw new Error('We could not verify your offer. Please try checkout again.');
        const updates = Object.fromEntries(gifts.map(item => [item.key, 0]));
        await request('cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }),
        });
        changed = true;
        current = await request('cart.js');
      }
      if (active && current.items.some(isGift)) throw new Error('Your cart is still updating. Please try checkout again.');
      if (changed) notifyCartUpdated();
      return current;
    })().finally(() => { cleanup = null; });
    return cleanup;
  }

  const offer = window.RastaVapeOffer = {
    code, active: () => active, observeCart, isGift, removeGifts,
    remove: async () => {
      await offer.ready;
      const cart = await request('cart.js');
      const remaining = (cart.discount_codes || []).filter(discount => normalize(discount.code) !== normalize(code));
      await request('cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: remaining.map(discount => discount.code).join(',') }),
      });
      notifyCartUpdated();
    },
    verifyCheckout: async () => {
      await offer.ready;
      return removeGifts(await request('cart.js'));
    },
  };
  offer.ready = active ? removeGifts().catch(() => {
    // Retry at the next gift sync/checkout; do not navigate on this failure.
    console.warn('The vape offer cart will be checked again before checkout.');
  }) : Promise.resolve();

  function renderOfferNotice() {
    const heading = document.querySelector('[id^="ProductInfo-"] .product__title, cart-items .title-wrapper-with-link');
    if (!heading) return;
    // This promotional copy belongs to the regular gift offer; show the
    // customer's actual offer in its place while their code is in the cart.
    for (const text of document.querySelectorAll('.product__text, .product-selection__subtitle')) {
      if (text.textContent.toLowerCase().includes('goodies on the house')) text.setAttribute('data-rasta-vape-offer-gift-copy', '');
    }
    const notice = document.createElement('aside');
    notice.className = 'rasta-vape-offer-notice';
    notice.setAttribute('aria-label', 'Vape offer');
    const copy = document.createElement('span');
    copy.textContent = '20% off 10 or more vapes. Free gifts are excluded from this offer.';
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.textContent = 'Remove offer';
    remove.addEventListener('click', async () => {
      remove.disabled = true;
      try { await offer.remove(); window.location.reload(); }
      catch (error) { window.alert(error.message); remove.disabled = false; }
    });
    notice.append(copy, remove);
    heading.after(notice);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', renderOfferNotice, { once: true });
  else renderOfferNotice();

  // Run before Tagada's capture listeners. Replay the original checkout control
  // after cart mutations settle, retaining Tagada and the membership gate.
  async function guardCheckout(event) {
    const target = event.type === 'submit' ? event.submitter : event.target?.closest?.(
      '#checkout, #CartDrawer-Checkout, button[name="checkout"], input[name="checkout"], a[href="/checkout"]'
    );
    if (!active || !target) return;
    if (replayTarget === target) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutBusy || target.disabled) return;
    checkoutBusy = true;
    target.setAttribute('aria-busy', 'true');
    try {
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.ensureCheckoutCartIsFinalized) await drawer.ensureCheckoutCartIsFinalized();
      const cartItems = document.querySelector('cart-items');
      if (cartItems?.waitForPendingCartRequests) await cartItems.waitForPendingCartRequests();
      await offer.verifyCheckout();
      const replay = target.id ? document.getElementById(target.id) || target : target;
      replayTarget = replay;
      if (event.type === 'submit') (replay.form || event.target).requestSubmit(replay);
      else replay.click();
    } catch (error) {
      const container = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (container) { container.textContent = error.message; container.setAttribute('role', 'alert'); }
      else window.alert(error.message);
    } finally {
      replayTarget = null;
      checkoutBusy = false;
      target.removeAttribute('aria-busy');
    }
  }
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);
})();
